<template>
  <section class="settings-section">
    <div class="container pt-5 px-3">
      <div class="row mt-5 pt-3">
        <!-- Sidebar -->
        <aside class="col-lg-3">
          <div class="sidebar-wrapper">

            <!-- Navigation Menu -->
            <nav class="settings-nav">
              <!-- Admin Menu -->
              <div v-if="isAdmin" class="nav-section">
                <h6 class="nav-title">ADMINISTRACIÓN</h6>
                <a href="/admin/productos" class="nav-link">
                  <i class="fas fa-box"></i>
                  <span>Productos</span>
                </a>
                <a href="/admin/pedidos" class="nav-link">
                  <i class="fas fa-shopping-cart"></i>
                  <span>Pedidos</span>
                </a>
                <a href="/admin/bancos" class="nav-link">
                  <i class="fas fa-university"></i>
                  <span>Bancos</span>
                </a>
                <a href="/admin/actividad" class="nav-link">
                  <i class="fas fa-history"></i>
                  <span>Actividad</span>
                </a>
                <a href="/admin/garantias" class="nav-link">
                  <i class="fas fa-shield-alt"></i>
                  <span>Garantías</span>
                </a>
                <a href="/admin/usuarios" class="nav-link">
                  <i class="fas fa-users"></i>
                  <span>Usuarios</span>
                </a>
              </div>

              <!-- User Menu -->
              <div class="nav-section">
                <h6 class="nav-title">MI CUENTA</h6>
                <a 
                  href="#orders" 
                  @click.prevent="setActiveSection('orders')"
                  :class="['nav-link', { active: activeSection === 'orders' }]"
                >
                  <i class="fas fa-shopping-bag"></i>
                  <span>Mis Pedidos</span>
                </a>
                <a 
                  href="#warranty" 
                  @click.prevent="setActiveSection('warranty')"
                  :class="['nav-link', { active: activeSection === 'warranty' }]"
                  v-if="!isAdmin"
                >
                  <i class="fas fa-seedling"></i>
                  <span>Garantía</span>
                </a>
                <a 
                  href="#settings" 
                  @click.prevent="setActiveSection('settings')"
                  :class="['nav-link', { active: activeSection === 'settings' }]"
                >
                  <i class="fas fa-cog"></i>
                  <span>Configuración</span>
                </a>
              </div>
            </nav>

            <!-- Logout Button -->
            <button @click="handleLogout" class="logout-btn fs-6 mt-5">
              <i class="fas fa-sign-out-alt"></i>
              <span>Cerrar Sesión</span>
            </button>
          </div>
        </aside>

        <!-- Main Content -->
        <main class="col-lg-9">
          <!-- Settings Section -->
          <div v-if="activeSection === 'settings'" class="content-section">
            <h2 class="section-title">Configuración de cuenta</h2>
            <div class="card d-flex">
              <div class="card-body px-0">
                <!-- Toggle Buttons -->
                <div class="mb-4 d-flex gap-3">
                  <button 
                    class="btn"
                    :class="showPasswordForm ? 'btn-outline-primary' : 'btn-primary'"
                    @click="showPasswordForm = false"
                  >
                    <i class="fas fa-user me-2"></i>
                    Datos Personales
                  </button>
                  <button 
                    class="btn"
                    :class="showPasswordForm ? 'btn-primary' : 'btn-outline-primary'"
                    @click="showPasswordForm = true"
                  >
                    <i class="fas fa-key me-2"></i>
                    Cambiar Contraseña
                  </button>
                </div>

                <!-- Personal Data Form -->
                <form v-if="!showPasswordForm" @submit.prevent="handleSubmit">
                  <div class="row mx-0">
                    <div class="col-md-6 px-0 pe-lg-2">
                      <div class="form-group">
                        <label>Nombre</label>
                        <input 
                          v-model="formData.first_name" 
                          type="text" 
                          class="form-control"
                          placeholder="Tu nombre"
                        >
                      </div>
                    </div>

                    <div class="col-md-6 ps-md-2 pe-0">
                      <div class="form-group">
                        <label>Apellido</label>
                        <input 
                          v-model="formData.last_name" 
                          type="text" 
                          class="form-control"
                          placeholder="Tu apellido"
                        >
                        </div>
                    </div>
                    
                    <div class="col-12 px-0">
                      <div class="form-group">
                        <label>Email</label>
                        <input 
                          v-model="formData.email" 
                          type="email" 
                          class="form-control"
                          placeholder="tu@email.com"
                        >
                      </div>
                    </div>
                  </div>

                  <div class="form-actions">
                    <button type="submit" class="btn btn-primary" :disabled="isLoading">
                      <i class="fas fa-save me-2"></i>
                      {{ isLoading ? 'Guardando...' : 'Guardar cambios' }}
                    </button>
                  </div>
                </form>

                <!-- Password Change Form -->
                <form v-else @submit.prevent="handlePasswordChange">
                  <div class="row mx-0">
                    <div class="col-12 px-0"> 
                      <div class="form-group">
                        <label>Contraseña Actual</label>
                        <div class="input-group">
                          <input 
                            :type="showCurrentPassword ? 'text' : 'password'" 
                            v-model="passwordForm.currentPassword" 
                            class="form-control"
                            placeholder="Ingresa tu contraseña actual"
                          >
                          <button 
                            type="button" 
                            class="btn btn-outline-secondary"
                            @click="showCurrentPassword = !showCurrentPassword"
                          >
                            <i :class="showCurrentPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                    
                    <div class="col-md-6 px-0 pe-md-2"> <!-- Ajustado padding -->
                      <div class="form-group">
                        <label>Nueva Contraseña</label>
                        <div class="input-group">
                          <input 
                            :type="showNewPassword ? 'text' : 'password'" 
                            v-model="passwordForm.newPassword" 
                            class="form-control"
                            placeholder="Ingresa tu nueva contraseña"
                          >
                          <button 
                            type="button" 
                            class="btn btn-outline-secondary"
                            @click="showNewPassword = !showNewPassword"
                          >
                            <i :class="showNewPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                    
                    <div class="col-md-6 ps-md-2 pe-0"> <!-- Ajustado padding -->
                      <div class="form-group">
                        <label>Confirmar Nueva Contraseña</label>
                        <div class="input-group">
                          <input 
                            :type="showConfirmPassword ? 'text' : 'password'" 
                            v-model="passwordForm.confirmPassword" 
                            class="form-control"
                            placeholder="Confirma tu nueva contraseña"
                          >
                          <button 
                            type="button" 
                            class="btn btn-outline-secondary"
                            @click="showConfirmPassword = !showConfirmPassword"
                          >
                            <i :class="showConfirmPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="form-actions">
                    <button type="submit" class="btn btn-primary" :disabled="isChangingPassword">
                      <i class="fas fa-key me-2"></i>
                      {{ isChangingPassword ? 'Cambiando...' : 'Cambiar Contraseña' }}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>

          <!-- Orders Section -->
          <div v-if="activeSection === 'orders'" class="content-section">
            <h2 class="section-title">Mis Pedidos</h2>
            <UserOrdersList />
          </div>

          <!-- Warranty Claims Section -->
          <div v-if="activeSection === 'warranty'" class="content-section">
            <h2 class="section-title">Garantía</h2>
            <p class="text-muted fs-6">
              Si tus semillas no germinaron correctamente, puedes iniciar un reclamo de garantía aquí. 
              Nuestro equipo revisará tu caso y te contactará a la brevedad.
            </p>
            <WarrantyClaims :isAdmin="isAdmin" />
          </div>
        </main>
      </div>
    </div>
    
    <!-- Photo Modal -->
    <div v-if="showPhotoModal" class="photo-modal" @click="closePhotoModal">
      <div class="photo-modal-content" @click.stop>
        <button class="close-button" @click="closePhotoModal">&times;</button>
        <img :src="currentPhoto" alt="Foto ampliada" />
      </div>
    </div>

    <!-- Verification Modal -->
    <div v-if="showVerificationForm" class="verification-overlay">
      <div class="verification-modal">
        <h3>Verificar cambio de email</h3>
        <p>Te hemos enviado un código de verificación a tu email actual.</p>
        
        <div class="form-group">
          <label>Código de verificación</label>
          <input 
            v-model="verificationCode"
            type="text"
            class="form-control"
            placeholder="Ingresa el código"
            maxlength="6"
          >
        </div>
        
        <div class="modal-actions">
          <button 
            class="btn btn-secondary"
            @click="showVerificationForm = false"
          >
            Cancelar
          </button>
          <button 
            class="btn btn-primary"
            @click="submitEmailChange"
            :disabled="!verificationCode"
          >
            Verificar y Cambiar
          </button>
        </div>
      </div>
    </div>

    <!-- Password Prompt Modal -->
    <div v-if="showPasswordModal" class="verification-overlay">
      <div class="verification-modal">
        <h3>Verificar Identidad</h3>
        <p>Por favor, ingresa tu contraseña actual para continuar:</p>
        
        <div class="form-group">
          <label>Contraseña actual</label>
          <input 
            v-model="currentPassword"
            type="password"
            class="form-control"
            placeholder="Ingresa tu contraseña"
          >
        </div>
        
        <div class="modal-actions">
          <button 
            class="btn btn-secondary"
            @click="handlePasswordCancel"
          >
            Cancelar
          </button>
          <button 
            class="btn btn-primary"
            @click="() => handlePasswordSubmit(currentPassword)"
            :disabled="!currentPassword"
          >
            Confirmar
          </button>
        </div>
      </div>
    </div>

    <!-- Agregar el loader después de los modales existentes -->
    <LoaderOverlay 
      v-if="isRequestingCode" 
      message="Enviando código de verificación..."
    />
  </section>
</template>

<script setup>
import LoaderOverlay from '@/components/LoaderOverlay.vue'
import { ref, reactive, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useToast } from 'vue-toastification'
import { getUserInfo, updateUserProfile, changePassword, requestVerificationCode, updateEmailWithVerification, verifyPermissions } from '@/services/auth'
import { useVuelidate } from '@vuelidate/core'
import { required, email, minLength, helpers } from '@vuelidate/validators'
import UserOrdersList from '@/components/UserOrdersList.vue'
import WarrantyClaims from '@/components/WarrantyClaims.vue'
import { useStore } from 'vuex'

const router = useRouter()
const toast = useToast()

const isAdmin = ref(false) // Inicializar como false
const activeSection = ref('orders') // Cambiar valor inicial a 'orders'
const isLoading = ref(false)

// Estado del formulario usando reactive para validaciones
const formData = reactive({
  first_name: '',
  last_name: '',
  email: '',
  new_password: '',
  confirm_password: ''
})

const rules = {
  first_name: { 
    required: helpers.withMessage('El nombre es requerido', required),
    minLength: helpers.withMessage('El nombre debe tener al menos 2 caracteres', minLength(2))
  },
  last_name: { 
    required: helpers.withMessage('El apellido es requerido', required),
    minLength: helpers.withMessage('El apellido debe tener al menos 2 caracteres', minLength(2))
  },
  email: {
    required: helpers.withMessage('El email es requerido', required),
    email: helpers.withMessage('Ingrese un email válido', email)
  },
}

const v$ = useVuelidate(rules, formData)

const setActiveSection = (section) => {
  activeSection.value = section
}

const store = useStore()
const showVerificationForm = ref(false)
const verificationCode = ref('')
const pendingEmail = ref('')
const isRequestingCode = ref(false)

const currentPassword = ref('')

const handleEmailChange = async (newEmail) => {
  try {
    isRequestingCode.value = true
    
    // Solo pedir contraseña para cambio de email
    const password = await showPasswordPrompt()
    if (!password) return
    
    pendingEmail.value = newEmail
    
    await requestVerificationCode('EMAIL_CHANGE', newEmail, password)
    
    showVerificationForm.value = true
    toast.success('Te hemos enviado un código de verificación al nuevo email')
  } catch (error) {
    toast.error(error?.error || 'Error al solicitar verificación')
  } finally {
    isRequestingCode.value = false
  }
}

const showPasswordPrompt = () => {
  return new Promise((resolve) => {
    showPasswordModal.value = false // Reset estado
    currentPassword.value = ''
    showPasswordModal.value = true
    
    handlePasswordSubmit.value = (password) => {
      showPasswordModal.value = false
      currentPassword.value = ''
      resolve(password)
    }
    
    handlePasswordCancel.value = () => {
      showPasswordModal.value = false
      currentPassword.value = ''
      resolve(null)
    }
  })
}

const submitEmailChange = async () => {
  try {
    isLoading.value = true
    
    const response = await updateEmailWithVerification(
      pendingEmail.value,
      verificationCode.value
    )
    
    // Actualizar store con los datos actualizados
    store.dispatch('setUser', response)
    
    // Actualizar el formulario con el nuevo email
    formData.email = pendingEmail.value
    
    // Limpiar estado
    showVerificationForm.value = false
    verificationCode.value = ''
    pendingEmail.value = ''
    
    toast.success('Email actualizado correctamente')
  } catch (error) {
    console.error('Error updating email:', error)
    toast.error(error?.error || 'Error al actualizar email')
  } finally {
    isLoading.value = false
  }
}

const handleSubmit = async () => {
  try {
    const isFormValid = await v$.value.$validate()
    if (!isFormValid) {
      const firstError = Object.values(v$.value.$errors)[0]
      if (firstError) {
        toast.error(firstError.$message)
      }
      return
    }

    // Si el email ha cambiado, iniciar proceso de verificación
    if (formData.email !== store.state.user?.email) {
      await handleEmailChange(formData.email)
      return
    }

    // Para cambios de nombre/apellido, actualizar directamente
    isLoading.value = true
    const response = await updateUserProfile({
      first_name: formData.first_name,
      last_name: formData.last_name
    })
    
    store.dispatch('setUser', response)
    toast.success('Nombre actualizado correctamente')
  } catch (error) {
    console.error('Error updating profile:', error)
    toast.error(error?.error || 'Error al actualizar el perfil')
  } finally {
    isLoading.value = false
  }
}

const handleLogout = () => {
  localStorage.removeItem('accessToken')
  localStorage.removeItem('isAdmin') 
  localStorage.removeItem('cart')
  localStorage.removeItem('userOrders')
  localStorage.removeItem('adminOrders')
  localStorage.removeItem('userPermissions')
  localStorage.removeItem('userPermissionsTimestamp')
  window.location.reload()
}

const showPasswordForm = ref(false)
const showCurrentPassword = ref(false)
const showNewPassword = ref(false)
const showConfirmPassword = ref(false)
const isChangingPassword = ref(false)

const passwordForm = reactive({
  currentPassword: '',
  newPassword: '',
  confirmPassword: ''
})

const handlePasswordChange = async () => {
  if (passwordForm.newPassword !== passwordForm.confirmPassword) {
    toast.error('Las contraseñas no coinciden')
    return
  }

  try {
    isChangingPassword.value = true
    await changePassword(passwordForm.currentPassword, passwordForm.newPassword)
    toast.success('Contraseña actualizada correctamente')
    
    // Limpiar formulario
    passwordForm.currentPassword = ''
    passwordForm.newPassword = ''
    passwordForm.confirmPassword = ''
    showPasswordForm.value = false
  } catch (error) {
    toast.error(error.message)
  } finally {
    isChangingPassword.value = false
  }
}

const showPasswordModal = ref(false)
const handlePasswordSubmit = ref(null)
const handlePasswordCancel = ref(null)

// Photo Modal state (required for the template)
const showPhotoModal = ref(false)
const currentPhoto = ref('')

const closePhotoModal = () => {
  showPhotoModal.value = false
}

onMounted(async () => {
  // Obtener la sección de la URL si existe
  const urlParams = new URLSearchParams(window.location.search);
  const section = urlParams.get('section');
  if (section) {
    activeSection.value = section;
  }

  try {
    // Verificar permisos desde el backend
    const permissions = await verifyPermissions()
    isAdmin.value = permissions.is_staff || permissions.is_superuser
    
    // Cargar información del usuario
    const user = await getUserInfo()
    formData.first_name = user.first_name || ''
    formData.last_name = user.last_name || ''
    formData.email = user.email || ''
  } catch (error) {
    console.error('Error loading user info:', error)
    if (error.response?.status === 401) {
      router.push('/login')
    } else {
      toast.error('Error al cargar la información')
    }
  }
})
</script>

<style scoped>
.settings-section {
  padding: 2rem 0;
  min-height: 100vh;
  background: var(--black-color);
}

.sidebar-wrapper {
  background: var(--black-color-3);
  border-radius: 1rem;
  padding: 1.5rem;
}

.col-md-6, .col-12 {
    padding-left: initial;
}

.user-profile {
  text-align: center;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.avatar-wrapper {
  position: relative;
  width: 100px;
  height: 100px;
  margin: 0 auto;
}

.user-avatar {
  width: 100px;
  height: 100px;
  border-radius: 50%;
  object-fit: cover;
}

.change-avatar-btn {
  position: absolute;
  bottom: 0;
  right: 0;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: var(--primary-color);
  border: none;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.user-name {
  color: var(--white-color);
  margin-top: 1rem;
}

.user-role {
  color: var(--white-color-2);
  font-size: 0.875rem;
}

.nav-section {
  margin-bottom: 1.5rem;
}

.nav-title {
  color: var(--white-color-2);
  font-size: 0.75rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
  padding-left: 0.5rem;
}

.nav-link {
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  color: var(--white-color);
  text-decoration: none;
  border-radius: 0.5rem;
  transition: all 0.3s ease;
  margin-bottom: 0.25rem;
}

.nav-link i {
  margin-right: 0.75rem;
  width: 20px;
  text-align: center;
}

.nav-link:hover,
.nav-link.active {
  background: var(--primary-color);
  color: white;
}

.logout-btn {
  width: 100%;
  padding: 0.75rem;
  background: rgba(255, 59, 48, 0.1);
  color: #ff3b30;
  border: none;
  border-radius: 0.5rem;
  margin-top: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  transition: all 0.3s ease;
}

.logout-btn:hover {
  background: rgba(255, 59, 48, 0.2);
}

.content-section {
  background: var(--black-color-3);
  border-radius: 1rem;
  padding: 2rem;
}

.section-title {
  color: var(--white-color);
  margin-bottom: 1.5rem;
}

.card {
  background: var(--black-color-3);
  border: none;
  border-radius: 1rem;
}

.form-group {
  margin-bottom: 1rem;
}

.form-group label {
  color: var(--white-color);
  margin-bottom: 0.5rem;
  display: block;
}

.form-control {
  background: var(--black-color-2);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: var(--white-color);
  border-radius: 0.5rem;
  padding: 0.75rem 1rem;
}

.form-control:focus {
  background: var(--black-color-2);
  border-color: var(--primary-color);
  color: var(--white-color);
  box-shadow: none;
}

.form-actions {
  margin-top: 2rem;
  display: flex;
  justify-content: flex-end;
}

.input-group .btn-outline-secondary {
  border-color: rgba(255, 255, 255, 0.1);
  color: var(--white-color-2);
}

.input-group .btn-outline-secondary:hover {
  background-color: rgba(255, 255, 255, 0.1);
  color: var(--white-color);
}

.input-group .form-control {
  border-right: none;
}

.input-group .btn {
  border-left: none;
}

.orders-skeleton {
  min-height: 200px;
}

.skeleton-order {
  height: 200px;
  background: linear-gradient(90deg, #333333 25%, #404040 50%, #333333 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 12px;
}

.order-card {
  background: var(--black-color-2);
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.order-header {
  padding: 1rem;
  background: rgba(0, 0, 0, 0.2);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.order-id {
  font-family: monospace;
  color: var(--white-color-2);
}

.order-content {
  padding: 1rem;
}

.product-item {
  display: flex;
  gap: 1rem;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  margin-bottom: 0.5rem;
  transition: transform 0.2s ease;
}

.product-item:hover {
  transform: translateX(5px);
}

.product-thumbnail {
  width: 60px;
  height: 60px;
  object-fit: contain;
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.2);
  padding: 0.25rem;
}

.info-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.5rem;
}

.info-row i {
  width: 16px;
  text-align: center;
}

.total-label {
  color: var(--white-color-2);
}

.total-amount {
  font-size: 1.2rem;
  font-weight: bold;
  color: var(--primary-color);
}

@media (max-width: 991px) {
  .sidebar-wrapper {
    margin-bottom: 2rem;
    position: static;
  }
}

.verification-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.verification-modal {
  background: var(--black-color-3);
  padding: 2rem;
  border-radius: var(--radius);
  width: 90%;
  max-width: 400px;
}

.modal-actions {
  display: flex;
  gap: 1rem;
  margin-top: 1.5rem;
}

.modal-actions button {
  flex: 1;
}

/* Warranty Styles */
.warranty-steps {
  margin: 2rem 0;
}

.warranty-step {
  display: flex;
  margin-bottom: 1.5rem;
  align-items: flex-start;
}

.step-number {
  background: var(--primary-color);
  color: white;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  margin-right: 1rem;
  flex-shrink: 0;
}

.step-content h6 {
  color: var(--white-color);
  margin-bottom: 0.5rem;
}

.step-content p {
  color: var(--white-color-2);
  margin-bottom: 0;
}

.warranty-claims-list {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.warranty-claim-card {
  background: var(--black-color-2);
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.claim-header {
  padding: 1rem;
  background: rgba(0, 0, 0, 0.2);
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.claim-id {
  font-family: monospace;
  color: var(--white-color-2);
  font-size: 0.9rem;
}

.claim-status {
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
}

.status-pending {
  background: rgba(255, 193, 7, 0.2);
  color: #ffc107;
}

.status-reviewing {
  background: rgba(0, 123, 255, 0.2);
  color: #0d6efd;
}

.status-approved {
  background: rgba(40, 167, 69, 0.2);
  color: #28a745;
}

.status-rejected {
  background: rgba(220, 53, 69, 0.2);
  color: #dc3545;
}

.status-resolved {
  background: rgba(108, 117, 125, 0.2);
  color: #6c757d;
}

.claim-details {
  padding: 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.description-text {
  font-size: 0.9rem;
  color: var(--white-color-2);
  max-height: 100px;
  overflow-y: auto;
}

.claim-photos {
  padding: 1rem;
}

.photo-grid {
  display: flex;
  gap: 0.5rem;
}

.photo-item {
  width: 80px;
  height: 80px;
  overflow: hidden;
  border-radius: 8px;
  cursor: pointer;
  position: relative;
}

.photo-item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.photo-item:hover img {
  transform: scale(1.1);
}

.photo-label {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  font-size: 0.7rem;
  padding: 2px;
  text-align: center;
}

.claim-actions {
  padding: 1rem;
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2rem;
  color: var(--white-color-2);
}

.photo-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
}

.photo-modal-content {
  max-width: 90%;
  max-height: 90%;
  position: relative;
}

.photo-modal-content img {
  max-width: 100%;
  max-height: 80vh;
  display: block;
}

.close-button {
  position: absolute;
  top: -40px;
  right: 0;
  background: none;
  border: none;
  color: white;
  font-size: 2rem;
  cursor: pointer;
}
</style>